import { ref, watch } from 'vue';
import type { BaseCanvasElement, BoundedTextElement } from '@/typings/customilyElements/canvasElements';

/**
 * Whether the text is a text to shape (see textShapeUtils). The Shape panel mutates the same (non
 *  reactive) fabric object and a shape forces multiline/align/allowEdit, so other panels resync on
 *  the object's 'modified' event.
 * @param onSync called with the element on every resync, to refresh the props a shape changes
 */
export const useTextShapeState = (
    getElement: () => BaseCanvasElement | undefined,
    onSync?: (element: BoundedTextElement) => void
) => {
    const hasTextShape = ref(false);

    watch(
        getElement,
        (element, _previous, onCleanup) => {
            hasTextShape.value = false;
            if (!element || element.type !== 'bounded-text') return;

            const boundedText = element as BoundedTextElement;
            // Only a text to shape needs the resync. shapeConfig is set at creation (constructor), so a
            //  plain text never becomes one while selected, and its panels keep working as before
            if (!boundedText.shapeConfig) return;

            const sync = () => {
                hasTextShape.value = !!boundedText.shapeConfig;
                // A throw would stop the object's other 'modified' listeners, undo/redo among them
                try {
                    onSync?.(boundedText);
                } catch (error) {
                    console.error(error);
                }
            };
            sync();
            element.on('modified', sync);
            onCleanup(() => element.off('modified', sync));
        },
        { immediate: true }
    );

    return { hasTextShape };
};
